import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  AlarmOutlined,
  ChatBubbleOutline,
  CompareArrows,
  DescriptionOutlined,
  ErrorOutline,
  FlagOutlined,
  LinearScale,
  VideoCameraFrontOutlined,
  PaidOutlined,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import useCaseList from "../../hooks/useCaseList";
import useErrorHandler from "../../hooks/useErrorHandler";
import { getCaseTimeline } from "../../api/timeline";
import { caseActions } from "../../config/action-config";

// One restrained colour per event family; the icon carries the rest of the meaning.
const EVENT_TYPES = {
  case: { label: "Case", Icon: FlagOutlined, color: "#005480" },
  document: { label: "Documents", Icon: DescriptionOutlined, color: "#6b7a90" },
  comment: { label: "Comments", Icon: ChatBubbleOutline, color: "#2f6fd6" },
  negotiation: { label: "Negotiation", Icon: CompareArrows, color: "#1b8a5a" },
  offer: { label: "Offers", Icon: PaidOutlined, color: "#1b8a5a" },
  dispute: { label: "Disputes", Icon: ErrorOutline, color: "#c2410c" },
  meeting: { label: "Meetings", Icon: VideoCameraFrontOutlined, color: "#7b5cff" },
  stage: { label: "Stages", Icon: LinearScale, color: "#0f766e" },
  deadline: { label: "Deadlines", Icon: AlarmOutlined, color: "#b45309" },
};

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const dayFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" });
const timeFmt = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit" });
const monthFmt = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });

// Start/end dates are stored as dates, not moments, so showing a clock time for them is noise.
const isDateOnly = (e) => e.type === "case" && /date$/i.test(e.title);

function groupByMonth(events) {
  const groups = [];
  events.forEach((e) => {
    const label = monthFmt.format(new Date(e.occurredAt));
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(e);
    else groups.push({ label, items: [e] });
  });
  return groups;
}

function EventCard({ event, upcoming }) {
  const [open, setOpen] = useState(false);
  const { Icon, color } = EVENT_TYPES[event.type] || EVENT_TYPES.case;
  const when = new Date(event.occurredAt);
  const long = (event.detail || "").length > 140;

  return (
    <li className="relative pl-12 pb-5 last:pb-0 list-none">
      <span
        className="absolute left-0 top-1 w-8 h-8 rounded-full grid place-items-center text-white ring-4 ring-white"
        style={{ backgroundColor: color }}
      >
        <Icon className="!text-[17px]" />
      </span>
      <Paper
        variant="outlined"
        className={`px-4 py-3 rounded-xl ${upcoming ? "border-dashed bg-[#fafcff]" : ""}`}
      >
        <Box className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <Typography className="font-semibold text-[14px] text-[#0d1b36]">{event.title}</Typography>
          <Typography className="text-[12px] text-[#6b7a90] whitespace-nowrap">
            {dayFmt.format(when)}
            {isDateOnly(event) ? "" : ` · ${timeFmt.format(when)}`}
          </Typography>
        </Box>

        {event.detail ? (
          <Typography
            className={`mt-1 text-[13px] text-[#344767] break-words ${!open && long ? "line-clamp-2" : ""}`}
          >
            {event.detail}
          </Typography>
        ) : null}
        {long ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="mt-1 p-0 text-[12px] font-semibold text-brand bg-transparent border-0 cursor-pointer"
          >
            {open ? "Show less" : "Show more"}
          </button>
        ) : null}

        {event.actor || event.amount !== null || upcoming ? (
          <Box className="mt-2 flex flex-wrap items-center gap-2">
            {event.actor ? <Chip size="small" label={event.actor} className="bg-[#eef3fb] text-[#0b3474]" /> : null}
            {event.amount !== null ? (
              <Chip size="small" label={money.format(event.amount)} className="bg-[#e7f6ee] text-[#1b6b47] font-semibold" />
            ) : null}
            {upcoming ? <Chip size="small" variant="outlined" label="Upcoming" /> : null}
          </Box>
        ) : null}
      </Paper>
    </li>
  );
}

export default function CaseTimeline() {
  const { caseId: routeCaseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const errorHandler = useErrorHandler();
  const { data: user } = useSelector((state) => state.user);
  const selected = useSelector((state) => state.cases.selected);
  const { caseList, setSelectedCaseId } = useCaseList();

  const caseId = routeCaseId || "";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(null); // null = all types
  const [newestFirst, setNewestFirst] = useState(true);

  // Arriving from the sidebar with a case already chosen elsewhere: jump straight to it.
  useEffect(() => {
    if (!routeCaseId && selected?.id) navigate(`/timeline/${selected.id}`, { replace: true });
  }, [routeCaseId, selected?.id]);

  useEffect(() => {
    if (!caseId || !user.token) {
      setEvents([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCaseTimeline(user.token, caseId)
      .then(({ data }) => !cancelled && setEvents(data.data))
      .catch((err) => !cancelled && (setEvents([]), errorHandler(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [caseId, user.token]);

  const onCaseChange = ({ target }) => {
    setEnabled(null);
    setSelectedCaseId(target.value);
    dispatch({ type: caseActions.SELECT, payload: { id: target.value } });
    navigate(target.value ? `/timeline/${target.value}` : "/timeline");
  };

  const counts = useMemo(() => {
    const c = {};
    events.forEach((e) => (c[e.type] = (c[e.type] || 0) + 1));
    return c;
  }, [events]);

  const visible = useMemo(() => {
    const list = events.filter((e) => !enabled || enabled.includes(e.type));
    list.sort((a, b) => new Date(a.occurredAt) - new Date(b.occurredAt));
    return newestFirst ? list.reverse() : list;
  }, [events, enabled, newestFirst]);

  const toggleType = (type) =>
    setEnabled((cur) => {
      const base = cur || Object.keys(counts);
      const next = base.includes(type) ? base.filter((t) => t !== type) : [...base, type];
      return next.length === Object.keys(counts).length ? null : next;
    });

  const now = Date.now();

  return (
    <Paper className="odr-card">
      <Box className="odr-card-header flex flex-wrap items-center justify-between gap-3">
        <Typography className="heading-title">Case Timeline</Typography>
        <FormControl size="small" className="w-full min-[600px]:w-[320px]">
          <InputLabel id="timeline-case-label">Case</InputLabel>
          <Select labelId="timeline-case-label" label="Case" value={caseId} onChange={onCaseChange}>
            <MenuItem value="">Select a case</MenuItem>
            {caseList.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box className="px-4 pb-6">
        {!caseId ? (
          <Box className="py-16 text-center text-[#6b7a90]">
            <TimelineIcon className="!text-[48px] opacity-40" />
            <Typography className="mt-2">Select a case to see its timeline.</Typography>
          </Box>
        ) : loading ? (
          <Box className="py-16 grid place-items-center">
            <CircularProgress size={28} />
          </Box>
        ) : !events.length ? (
          <Typography className="py-16 text-center text-[#6b7a90]">No activity recorded for this case yet.</Typography>
        ) : (
          <>
            <Box className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <Box className="flex flex-wrap gap-2">
                {Object.keys(EVENT_TYPES)
                  .filter((t) => counts[t])
                  .map((t) => {
                    const on = !enabled || enabled.includes(t);
                    return (
                      <Chip
                        key={t}
                        clickable
                        size="small"
                        onClick={() => toggleType(t)}
                        label={`${EVENT_TYPES[t].label} · ${counts[t]}`}
                        variant={on ? "filled" : "outlined"}
                        className={on ? "!bg-brand-hover !text-brand" : "!text-[#6b7a90]"}
                      />
                    );
                  })}
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={newestFirst ? "new" : "old"}
                onChange={(_, v) => v && setNewestFirst(v === "new")}
              >
                <ToggleButton value="new">Newest first</ToggleButton>
                <ToggleButton value="old">Oldest first</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {visible.length ? (
              groupByMonth(visible).map((group) => (
                <section key={group.label} className="mb-6 last:mb-0">
                  <Typography className="mb-3 text-[12px] font-bold uppercase tracking-[0.08em] text-[#6b7a90]">
                    {group.label}
                  </Typography>
                  <ul className="relative m-0 p-0 before:content-[''] before:absolute before:left-[15px] before:top-2 before:bottom-2 before:w-0.5 before:bg-[#dbe4f0]">
                    {group.items.map((e, i) => (
                      <EventCard key={`${e.type}-${e.occurredAt}-${i}`} event={e} upcoming={new Date(e.occurredAt) > now && !/^Deadline met/.test(e.title)} />
                    ))}
                  </ul>
                </section>
              ))
            ) : (
              <Typography className="py-10 text-center text-[#6b7a90]">No events match the selected filters.</Typography>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}
